import { useQuery } from '@tanstack/react-query'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { BiFilter } from 'react-icons/bi'

import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@/_components/ui'
import { cn } from '@/_lib/utils'
import { useSearchParams } from '@/features/_hooks/use-search-params'
import { queries } from '@/features/_lib/react-query'

export function CompanyFacilityFilter() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const { searchParams, setSearchParams } = useSearchParams()

  const { data: companiesData } = useQuery(queries.staffCompanies.list)
  const { data: facilities } = useQuery(queries.staffFacilities.list)

  const companies = companiesData?.companies ?? []

  const facilitiesByCompany = useMemo(() => {
    const map = new Map<string, typeof facilities>()
    for (const f of facilities ?? []) {
      const list = map.get(f.companyId) ?? []
      list.push(f)
      map.set(f.companyId, list)
    }
    return map
  }, [facilities])

  const facilityIdParam = (searchParams as Record<string, unknown>)?.facilityId as
    | string
    | undefined

  useEffect(() => {
    setSelectedIds(new Set(facilityIdParam ? facilityIdParam.split(',') : []))
  }, [facilityIdParam])

  const pushToUrl = (ids: Set<string>) => {
    setSearchParams({
      facilityId: ids.size ? Array.from(ids).join(',') : undefined,
    })
  }

  const handleToggleFacility = (facilityId: string) => {
    const next = new Set(selectedIds)
    if (next.has(facilityId)) {
      next.delete(facilityId)
    } else {
      next.add(facilityId)
    }
    setSelectedIds(next)
    pushToUrl(next)
  }

  const handleSelectAllCompany = (companyId: string) => {
    const companyFacilities = facilitiesByCompany.get(companyId) ?? []
    const companyFacilityIds = companyFacilities.map((f) => f.id)
    const allSelected = companyFacilityIds.every((id) => selectedIds.has(id))

    const next = new Set(selectedIds)
    if (allSelected) {
      for (const id of companyFacilityIds) next.delete(id)
    } else {
      for (const id of companyFacilityIds) next.add(id)
    }
    setSelectedIds(next)
    pushToUrl(next)
  }

  const handleClear = () => {
    setSelectedIds(new Set())
    pushToUrl(new Set())
  }

  const selectedCount = selectedIds.size

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 font-medium">
          <BiFilter className="mr-2 text-muted-foreground" size={18} />
          Unidade
          {selectedCount > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge className="rounded-sm bg-transparent px-1 font-bold text-brand-green-primary-5 hover:bg-transparent">
                {selectedCount}
              </Badge>
            </>
          )}
          <ChevronDownIcon className="ml-1 h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Pesquise por empresa ou unidade" className="h-9" />
          <CommandList className="scrollbar">
            <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
            {companies.map((company) => {
              const companyFacilities = facilitiesByCompany.get(company.id) ?? []
              const allSelected =
                companyFacilities.length > 0 &&
                companyFacilities.every((f) => selectedIds.has(f.id))
              return (
                <div key={company.id} className="group/company">
                  <CommandGroup
                    heading={
                      <div className="flex w-full items-center justify-between text-xs text-muted-foreground">
                        <span>{company.name}</span>
                        {companyFacilities.length > 1 && (
                          <button
                            type="button"
                            className={cn(
                              'cursor-pointer text-[10px] font-medium tracking-wide opacity-0 transition-opacity group-hover/company:opacity-100 hover:text-foreground',
                              allSelected && 'text-brand-green-primary-5 opacity-100',
                            )}
                            onClick={() => handleSelectAllCompany(company.id)}
                          >
                            Selecionar tudo
                          </button>
                        )}
                      </div>
                    }
                  >
                    {companyFacilities.map((facility) => {
                      const isSelected = selectedIds.has(facility.id)
                      return (
                        <CommandItem
                          key={facility.id}
                          value={`${facility.name} ${company.name}`}
                          onSelect={() => handleToggleFacility(facility.id)}
                        >
                          <div
                            className={cn(
                              'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                              isSelected
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-muted-foreground/40 [&_svg]:invisible',
                            )}
                          >
                            <CheckIcon className="h-4 w-4" />
                          </div>
                          <span>{facility.name}</span>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </div>
              )
            })}
          </CommandList>
          {selectedCount > 0 && (
            <>
              <CommandSeparator />
              <div className="flex justify-end p-2">
                <button
                  onClick={handleClear}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Limpar filtros
                </button>
              </div>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
